import { z } from 'zod';
const money = (max) => z.number().int().min(0).max(max);
/**
 * The driver's private pricing sheet. Riders never see it; the driver uses it to work out a
 * quote and then types the number. Per-vehicle, as California charter-party rules require.
 */
export const RateCardInputSchema = z.object({
    hourlyCents: money(100_000),
    /** Minimum hours billed on an hourly job. */
    minHours: z.number().min(0).max(24),
    perMileCents: money(10_000),
    perMinuteCents: money(1_000),
    minFareCents: money(100_000),
    airportFeeCents: money(50_000),
    waitPerMinCents: money(1_000),
    /** Percentage added to the total for gratuity when the driver prices it in. */
    gratuityPct: z.number().int().min(0).max(50),
    notes: z.string().trim().max(400).nullable().optional(),
});
export const DEFAULT_RATE_CARD = {
    hourlyCents: 9500,
    minHours: 3,
    perMileCents: 350,
    perMinuteCents: 75,
    minFareCents: 7500,
    airportFeeCents: 1500,
    waitPerMinCents: 100,
    gratuityPct: 0,
    notes: null,
};
export const EstimateInputSchema = z.object({
    mode: z.enum(['distance', 'hourly']),
    miles: z.number().min(0).max(2000).optional(),
    minutes: z.number().min(0).max(24 * 60).optional(),
    hours: z.number().min(0).max(48).optional(),
    airport: z.boolean().optional(),
    waitMinutes: z.number().min(0).max(24 * 60).optional(),
    /** Round the result up to the nearest this many dollars (0 = exact). */
    roundToDollars: z.number().int().min(0).max(100).optional(),
});
/** Pure arithmetic from the driver's own numbers. Nothing here adjusts, surges or discounts. */
export function estimate(card, input) {
    const lines = [];
    if (input.mode === 'hourly') {
        const hours = Math.max(input.hours ?? 0, card.minHours);
        lines.push({ label: `${hours} hr × ${(card.hourlyCents / 100).toFixed(2)}/hr${(input.hours ?? 0) < card.minHours ? ` (${card.minHours} hr minimum)` : ''}`, cents: Math.round(hours * card.hourlyCents) });
    }
    else {
        const miles = input.miles ?? 0;
        const minutes = input.minutes ?? 0;
        if (miles)
            lines.push({ label: `${miles} mi × ${(card.perMileCents / 100).toFixed(2)}/mi`, cents: Math.round(miles * card.perMileCents) });
        if (minutes && card.perMinuteCents)
            lines.push({ label: `${minutes} min × ${(card.perMinuteCents / 100).toFixed(2)}/min`, cents: Math.round(minutes * card.perMinuteCents) });
    }
    if (input.airport && card.airportFeeCents)
        lines.push({ label: 'Airport fee', cents: card.airportFeeCents });
    if (input.waitMinutes && card.waitPerMinCents)
        lines.push({ label: `${input.waitMinutes} min wait × ${(card.waitPerMinCents / 100).toFixed(2)}/min`, cents: Math.round(input.waitMinutes * card.waitPerMinCents) });
    let subtotalCents = lines.reduce((a, l) => a + l.cents, 0);
    let minimumApplied = false;
    if (subtotalCents < card.minFareCents) {
        lines.push({ label: 'Minimum fare', cents: card.minFareCents - subtotalCents });
        subtotalCents = card.minFareCents;
        minimumApplied = true;
    }
    const gratuityCents = Math.round((subtotalCents * card.gratuityPct) / 100);
    let totalCents = subtotalCents + gratuityCents;
    const r = (input.roundToDollars ?? 0) * 100;
    if (r > 0)
        totalCents = Math.ceil(totalCents / r) * r;
    return { lines, subtotalCents, gratuityCents, totalCents, minimumApplied };
}
//# sourceMappingURL=rate-calc.js.map